import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileHubApi } from '../lupp.service';
import { LUPP_QUERY_KEYS } from '../constants.lupp';
import { triggerBlobDownload } from '../../../../utils/tools';
import type {
  FileUploadRequest,
  FileUploadResponse,
  ConfirmFileUploadResponse,
  FileDownloadResponse,
  FileMetadata,
  DeleteFileResponse,
  SearchFilesRequest,
  SearchFilesResponse,
  SfdcFileDownloadResponse,
} from './dto.fileUpload';

/*
|--------------------------------------------------------------------------
| UPLOAD FILE
|--------------------------------------------------------------------------
| POST /filehub/upload
| Initiates a file upload and returns presigned PUT URL (valid for 15 minutes)
*/

export type UseUploadFileParams = {
  onSuccess?: (data: FileUploadResponse) => void;
  onError?: (error: Error) => void;
};

export const useUploadFile = (params?: UseUploadFileParams) => {
  return useMutation({
    mutationFn: async (data: FileUploadRequest) => {
      const response = await FileHubApi.post<FileUploadRequest, FileUploadResponse>(
        '/shared/filehub/upload',
        data,
        { extractData: (response) => response as unknown as FileUploadResponse }
      );
      return response;
    },
    onSuccess: params?.onSuccess,
    onError: params?.onError,
  });
};

/*
|--------------------------------------------------------------------------
| CONFIRM FILE UPLOAD
|--------------------------------------------------------------------------
| POST /shared/filehub/upload/{fileId}/confirm
| Confirms that file has been uploaded to S3 and changes status to AVAILABLE
| Returns 200 OK (empty response)
*/

export type UseConfirmFileUploadParams = {
  onSuccess?: (fileId: string) => void;
  onError?: (error: Error) => void;
};

export const useConfirmFileUpload = (params?: UseConfirmFileUploadParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (fileId: string) => {
      await FileHubApi.post<undefined, ConfirmFileUploadResponse>(
        `/shared/filehub/upload/${fileId}/confirm`,
        undefined
      );
      return fileId;
    },
    onSuccess: (fileId) => {
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.FILES] });
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.FILE_METADATA, fileId] });
      params?.onSuccess?.(fileId);
    },
    onError: params?.onError,
  });
};

/*
|--------------------------------------------------------------------------
| GET DOWNLOAD URL
|--------------------------------------------------------------------------
| GET /shared/filehub/files/{fileId}/download
| Returns presigned GET URL for downloading (valid for 60 minutes)
*/

export type UseGetDownloadUrlParams = {
  fileId: string;
  enabled?: boolean;
};

export const useGetDownloadUrl = ({ fileId, enabled = true }: UseGetDownloadUrlParams) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.FILE_DOWNLOAD, fileId],
    queryFn: async () => {
      const response = await FileHubApi.get<FileDownloadResponse>(
        `/shared/filehub/files/${fileId}/download`
      );
      return response;
    },
    enabled: enabled && !!fileId,
    staleTime: 30 * 60 * 1000, // 30 minutes (half of 60 min TTL)
  });
};

/*
|--------------------------------------------------------------------------
| GET FILE METADATA
|--------------------------------------------------------------------------
| GET /shared/filehub/files/{fileId}
| Returns file metadata
*/

export type UseGetFileMetadataParams = {
  fileId: string;
  enabled?: boolean;
};

export const useGetFileMetadata = ({ fileId, enabled = true }: UseGetFileMetadataParams) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.FILE_METADATA, fileId],
    queryFn: async () => {
      const response = await FileHubApi.get<FileMetadata>(`/shared/filehub/files/${fileId}`);
      return response;
    },
    enabled: enabled && !!fileId,
  });
};

/*
|--------------------------------------------------------------------------
| DELETE FILE
|--------------------------------------------------------------------------
| DELETE /shared/filehub/files/{fileId}?reason={reason}
| Soft deletes file (marks as DELETED, doesn't physically remove)
| Returns 204 No Content on success
*/

export type UseDeleteFileParams = {
  onSuccess?: (fileId: string) => void;
  onError?: (error: Error) => void;
};

export const useDeleteFile = (params?: UseDeleteFileParams) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ fileId, reason }: { fileId: string; reason?: string }) => {
      const url = reason
        ? `/shared/filehub/files/${fileId}?reason=${encodeURIComponent(reason)}`
        : `/shared/filehub/files/${fileId}`;
      await FileHubApi.delete<undefined, DeleteFileResponse>(url);
      return fileId;
    },
    onSuccess: (fileId) => {
      queryClient.invalidateQueries({
        queryKey: [LUPP_QUERY_KEYS.FILE_METADATA, fileId],
      });
      queryClient.invalidateQueries({ queryKey: [LUPP_QUERY_KEYS.FILES] });
      params?.onSuccess?.(fileId);
    },
    onError: params?.onError,
  });
};

/*
|--------------------------------------------------------------------------
| SEARCH FILES
|--------------------------------------------------------------------------
| POST /shared/filehub/files/search
| Search/filter files by customerId, category, status, date range, mimeType
*/

export type UseSearchFilesParams = SearchFilesRequest & {
  enabled?: boolean;
};

export const useSearchFiles = ({ enabled = true, ...searchParams }: UseSearchFilesParams = {}) => {
  return useQuery({
    queryKey: [LUPP_QUERY_KEYS.FILES, searchParams],
    queryFn: async () => {
      const response = await FileHubApi.post<SearchFilesRequest, SearchFilesResponse>(
        '/shared/filehub/files/search',
        searchParams
      );
      return response;
    },
    enabled,
  });
};

/*
|--------------------------------------------------------------------------
| DOWNLOAD FILE UTILITY
|--------------------------------------------------------------------------
| Utility function to download a file from FileHub
| Fetches the presigned URL and triggers browser download
*/

/**
 * Downloads a file from FileHub using the presigned download URL
 *
 * This is a shared utility to avoid duplicating file download logic across components.
 * Uses the FileHub download endpoint to get a presigned URL, then triggers browser download.
 *
 * @param fileId - The FileHub file identifier
 * @param fileName - The original filename for the download
 * @returns Promise that resolves when download is triggered, rejects on error
 * @throws Error if the download URL cannot be retrieved
 * @example
 * ```typescript
 * // In a component
 * const handleDocumentClick = async (fileId: string, fileName: string) => {
 *   try {
 *     await downloadFileFromFilehub(fileId, fileName);
 *   } catch (error) {
 *     setError('Failed to download file');
 *   }
 * };
 * ```
 */
export const downloadFileFromFilehub = async (fileId: string, fileName: string): Promise<void> => {
  // Use FileHubApi.instance directly to get raw Axios response with .data property
  const response = await FileHubApi.instance.get<string>(
    `/shared/filehub/files/${fileId}/download`
  );
  const downloadUrl = response.data;

  if (!downloadUrl) {
    throw new Error('Download URL not available');
  }

  // Fetch as blob to avoid S3 ISO-8859-1 errors with Unicode filenames in presigned URLs
  const fileResponse = await fetch(downloadUrl);
  if (!fileResponse.ok) {
    throw new Error(`Failed to fetch file: ${fileResponse.status}`);
  }
  const blob = await fileResponse.blob();
  triggerBlobDownload(blob, fileName);
};

/*
|--------------------------------------------------------------------------
| SFDC FILE DOWNLOAD UTILITIES
|--------------------------------------------------------------------------
| GET /shared/filehub/v1/files/download?tenant=&sfdcDeliveryId=&sfdcFileId=
| Downloads a Salesforce-sourced file registered in filehub.
| Returns base64-encoded file content — decoded here before use.
*/

/**
 * Fetches a Salesforce-sourced file from filehub and returns it as a Blob.
 * Use this when you need the raw Blob to either save or open in a new tab.
 *
 * Supports all MIME types returned by SFDC: PDFs, images (jpeg/png/gif/webp),
 * videos (mp4/quicktime/avi), spreadsheets, Word docs, etc.
 * The browser controls display/save behaviour based on the Blob's MIME type.
 */
export const fetchSfdcFileBlob = async (
  sfdcDeliveryId: string,
  sfdcFileId: string
): Promise<{ blob: Blob; fileName: string }> => {
  const response = await FileHubApi.instance.get<SfdcFileDownloadResponse>(
    `/shared/filehub/v1/files/download`,
    { params: { tenant: 'lucid-partner-us', sfdcDeliveryId, sfdcFileId } }
  );

  const { base64Content, mimeType, fileNameOriginal } = response.data;

  if (!base64Content) {
    throw new Error(`Empty file content received for sfdcFileId=${sfdcFileId}`);
  }

  // Fall back to a generic binary type if the server omits mimeType.
  // The browser will then prompt a save-as dialog rather than trying to render inline.
  const resolvedMimeType = mimeType?.trim() || 'application/octet-stream';

  const byteChars = atob(base64Content);
  const byteNumbers = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) {
    byteNumbers[i] = byteChars.charCodeAt(i);
  }
  const blob = new Blob([byteNumbers], { type: resolvedMimeType });

  return { blob, fileName: fileNameOriginal || '' };
};

/**
 * Downloads a Salesforce-sourced file from filehub directly to the browser.
 *
 * @param sfdcDeliveryId - The SFDC delivery identifier (`sfdcDeliveryIdentifier` on OrderDetailsResponse)
 * @param sfdcFileId - The SFDC file identifier (`sfdcFileIdentifier` on ModuleFileReference)
 * @param fallbackFileName - Used if the server does not return a filename
 */
export const downloadSfdcFileFromFilehub = async (
  sfdcDeliveryId: string,
  sfdcFileId: string,
  fallbackFileName: string
): Promise<void> => {
  const { blob, fileName } = await fetchSfdcFileBlob(sfdcDeliveryId, sfdcFileId);
  triggerBlobDownload(blob, fileName || fallbackFileName);
};
